/**
 * Presentation Usage Tracker Service
 * ---------------------------------
 * This service calculates presentation usage based on actual presentation data
 * from the last 30 days, rather than maintaining a separate counter.
 */

import { getUserPresentations } from './user-presentations-service';
import { isPremiumSubscription } from './subscription-check-service';

// Constants
export const FREE_PLAN_MONTHLY_LIMIT = 3;

// Debug toggle: set to false to disable console logs in production
const DEBUG = false;
function dbgLog(...args) { if (DEBUG) console.log(...args); }
function dbgWarn(...args) { if (DEBUG) console.warn(...args); }

/**
 * Get presentations created in the last N days
 * @param {number} days - Number of days to look back
 * @returns {Promise<Array>} Array of presentations in the specified time period
 */
export async function getPresentationsInLastDays(days = 30) {
  try {
    // Get all user presentations
    const allPresentations = await getUserPresentations();
    
    // Calculate the cutoff date (N days ago)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    // Filter presentations created after the cutoff date
    const recentPresentations = allPresentations.filter(
      presentation => presentation.timestamp >= cutoffDate
    );
    
    dbgLog(`[PresentationTracker] Found ${recentPresentations.length} presentations in the last ${days} days`);
    return recentPresentations;
  } catch (error) {
    dbgWarn('[PresentationTracker] Error getting recent presentations:', error);
    return [];
  }
}

/**
 * Get the count of presentations created in the last 30 days
 * @returns {Promise<number>} Number of presentations created in the last 30 days
 */
export async function getMonthlyPresentationCount() {
  try {
    const recentPresentations = await getPresentationsInLastDays(30);
    return recentPresentations.length;
  } catch (error) {
    dbgWarn('[PresentationTracker] Error counting monthly presentations:', error);
    return 0;
  }
}

/**
 * Check if the user has reached the monthly presentation limit
 * @returns {Promise<boolean>} True if the limit is reached, false otherwise
 */
export async function hasReachedMonthlyLimit() {
  // Premium users have no limit
  if (isPremiumSubscription()) {
    dbgLog('[PresentationTracker] User is premium, no limit applies');
    return false;
  }
  
  const currentUsage = await getMonthlyPresentationCount();
  const hasReached = currentUsage >= FREE_PLAN_MONTHLY_LIMIT;
  
  dbgLog(`[PresentationTracker] Current usage: ${currentUsage}/${FREE_PLAN_MONTHLY_LIMIT}, limit reached: ${hasReached}`);
  return hasReached;
}

/**
 * Get the number of presentations remaining for the current month
 * @returns {Promise<number|null>} Number of presentations remaining, or null for premium users
 */
export async function getRemainingPresentations() {
  // Premium users have unlimited presentations
  if (isPremiumSubscription()) {
    return null;
  }
  
  const currentUsage = await getMonthlyPresentationCount();
  return Math.max(0, FREE_PLAN_MONTHLY_LIMIT - currentUsage);
}

/**
 * Get detailed usage statistics for presentations
 * @returns {Promise<Object>} Usage statistics object
 */
export async function getUsageStatistics() {
  try {
    const isPremium = isPremiumSubscription();
    const last30Days = await getPresentationsInLastDays(30);
    const last7Days = last30Days.filter(p => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return p.timestamp >= sevenDaysAgo;
    });
    
    // Get today's presentations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayPresentations = last30Days.filter(p => p.timestamp >= today);
    
    return {
      isPremium,
      total30Days: last30Days.length,
      total7Days: last7Days.length,
      today: todayPresentations.length,
      limit: isPremium ? null : FREE_PLAN_MONTHLY_LIMIT,
      remaining: isPremium ? null : Math.max(0, FREE_PLAN_MONTHLY_LIMIT - last30Days.length),
      limitReached: !isPremium && last30Days.length >= FREE_PLAN_MONTHLY_LIMIT
    };
  } catch (error) {
    dbgWarn('[PresentationTracker] Error getting usage statistics:', error);
    return {
      isPremium: isPremiumSubscription(),
      total30Days: 0,
      total7Days: 0,
      today: 0,
      limit: isPremiumSubscription() ? null : FREE_PLAN_MONTHLY_LIMIT,
      remaining: isPremiumSubscription() ? null : FREE_PLAN_MONTHLY_LIMIT,
      limitReached: false
    };
  }
}

// Export as default object for easier importing
const presentationUsageTracker = {
  FREE_PLAN_MONTHLY_LIMIT,
  getPresentationsInLastDays,
  getMonthlyPresentationCount,
  hasReachedMonthlyLimit,
  getRemainingPresentations,
  getUsageStatistics
};

export default presentationUsageTracker;
